import './Wizard.scss'
import { useEffect, useId, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import { MdOutlineArrowBack, MdOutlineArrowForward, MdOutlineCheck } from 'react-icons/md'
import { Button } from '../Button'
import { clx } from '../clx'
import { Stepper, type WizardStep } from './Stepper'

export interface WizardProps extends Omit<ComponentProps<'div'>, 'children'> {
  steps: readonly WizardStep[]
  /** Zero-based index of the current step. */
  current: number
  onBack: () => void
  /** Next (or the submit action on the last step). May be async: while it runs both buttons are blocked. */
  onNext?: () => unknown
  /** False really disables Next (the `disabled` attribute). */
  canProceed?: boolean
  /** Why Next is disabled; linked to it through aria-describedby. */
  blockedReason?: ReactNode
  /** External pending state, e.g. react-hook-form's isSubmitting. */
  isPending?: boolean
  /** On the last step, render Next as a `type="submit"` button (the form submits; onNext is not called). */
  isSubmit?: boolean
  backLabel?: string
  nextLabel?: string
  submitLabel?: string
  /** The current step's content. */
  children: ReactNode
}

/**
 * Multi-step form shell (the portal's ra-form-layout WizardForm): a Stepper,
 * the step heading (focused when the step changes), the content and a
 * Previous / Next toolbar. The caller owns `current` and the form.
 */
export function Wizard({
  steps,
  current,
  onBack,
  onNext,
  canProceed = true,
  blockedReason,
  isPending = false,
  isSubmit = false,
  backLabel = 'Previous',
  nextLabel = 'Next',
  submitLabel = 'Save',
  children,
  className,
  ...rest
}: WizardProps) {
  const base = `wizard-${useId()}`
  const headingRef = useRef<HTMLHeadingElement>(null)
  const previous = useRef(current)
  const mounted = useRef(true)
  const [isRunning, setRunning] = useState(false)

  const isLastStep = current >= steps.length - 1
  const isBusy = isPending || isRunning
  const step = steps[current]
  const isBlocked = !canProceed
  const reasonId = `${base}-reason`

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (previous.current === current) return
    previous.current = current
    headingRef.current?.focus()
  }, [current])

  const next = async () => {
    if (!onNext || isBusy || isBlocked) return
    const result = onNext()
    if (!(result instanceof Promise)) return
    setRunning(true)
    try {
      await result
    } finally {
      if (mounted.current) setRunning(false)
    }
  }

  const isSubmitButton = isLastStep && isSubmit

  return (
    <div {...rest} className={clx('wizard', className)}>
      <Stepper steps={steps} current={current} className="wizard__stepper" />
      <section className="wizard__step" aria-labelledby={`${base}-heading`}>
        <h2 id={`${base}-heading`} ref={headingRef} tabIndex={-1} className="wizard__heading">
          {step?.title}
        </h2>
        <div className="wizard__content">{children}</div>
      </section>
      <div className="wizard__toolbar">
        {current > 0 && (
          <Button
            disabled={isBusy}
            icon={<MdOutlineArrowBack />}
            onClick={onBack}
            className="wizard__back"
          >
            {backLabel}
          </Button>
        )}
        <div className="wizard__next">
          {isBlocked && blockedReason && (
            <p id={reasonId} className="wizard__reason">
              {blockedReason}
            </p>
          )}
          <Button
            variant="primary"
            icon={isLastStep ? <MdOutlineCheck /> : <MdOutlineArrowForward />}
            type={isSubmitButton ? 'submit' : 'button'}
            disabled={isBlocked}
            isLoading={isBusy}
            aria-describedby={isBlocked && blockedReason ? reasonId : undefined}
            {...(!isSubmitButton && {
              onClick: () => {
                void next()
              }
            })}
          >
            {isLastStep ? submitLabel : nextLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
